import { ListFilter } from 'lucide-react';
import { useId, useMemo, useState } from 'react';

import type { FilterOption } from '@/types/roles';

import { FilterOptionItem } from '@/components/configuration-modal/filter-option-item';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export type MultiSelectFilterPopoverProps = {
  label: string;
  options: FilterOption[];
  selectedIds: Set<string>;
  onToggle: (id: string) => void;
  onSelectAll: (ids: string[]) => void;
  onClear: () => void;
};

const predefinedFilterSurfaceClass = cn(
  'border border-chat-send-stroke bg-chat-message-bg shadow-md'
);

export function MultiSelectFilterPopover({
  label,
  options,
  selectedIds,
  onToggle,
  onSelectAll,
  onClear,
}: MultiSelectFilterPopoverProps) {
  const [open, setOpen] = useState(false);
  const baseId = useId();
  const allRoleId = 'all';
  const allIds = useMemo(
    () => options.filter(({ id }) => id !== allRoleId).map(({ id }) => id),
    [options, allRoleId]
  );
  const showAll = useMemo(
    () =>
      options.some((option) => option.id !== allRoleId) &&
      options.every((option) => option.id === allRoleId || selectedIds.has(option.id)),
    [options, selectedIds]
  );

  function handleAllCheckboxChange(checked: boolean) {
    if (checked) {
      onSelectAll(allIds);
    } else {
      onClear();
    }
  }

  function handleOptionCheckedChange(option: FilterOption, checked: boolean) {
    if (option.id === allRoleId) {
      handleAllCheckboxChange(checked);

      return;
    }

    onToggle(option.id);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className={cn('gap-2', predefinedFilterSurfaceClass)}
        >
          <ListFilter className="h-4 w-4 shrink-0" aria-hidden />
          <span>{label}</span>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className={cn('w-72 p-0', predefinedFilterSurfaceClass)}
        onOpenAutoFocus={(event) => event.preventDefault()}
      >
        <div
          className="scrollbar-custom flex max-h-64 flex-col gap-2 overflow-y-auto p-2"
          role="group"
          aria-label={label}
          onWheel={(e) => e.stopPropagation()}
        >
          {options.map((option: FilterOption) => {
            const isAllSelected = option.id === allRoleId;

            return (
              <FilterOptionItem
                key={option.id}
                option={option}
                checked={isAllSelected ? showAll : selectedIds.has(option.id)}
                onCheckedChange={(checked) => handleOptionCheckedChange(option, checked)}
                baseId={baseId}
              />
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
